import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import { FileText, FileUp, Link2, Mic, NotebookPen } from 'lucide-react';
import type * as React from 'react';

export type ElementTileKind = 'interview' | 'notes' | 'document' | 'imported' | 'automatic';

const FILL_BY_KIND: Readonly<Record<ElementTileKind, string>> = Object.freeze({
	interview: theme.colors.accent,
	notes: theme.colors.validated,
	document: theme.tints.amber.solid,
	imported: theme.tints.teal.solid,
	automatic: theme.colors.inkSecondary,
});

const tileStyle = (fill: string, size: string) => css`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: ${size};
	height: ${size};
	border-radius: ${theme.radii.small};
	background-color: ${fill};
	color: ${theme.colors.surface};
`;

type Props = {
	readonly kind: ElementTileKind;
	/** CSS length of the square side. */
	readonly size?: string;
};

/** Colored square carrying the icon of what an element is: interview, dictated note, document, imported document, carried-over document. */
const ElementTile: React.FC<Props> = ({ kind, size = '2.5rem' }) => {
	const iconSize = 18;
	const icons: Readonly<Record<ElementTileKind, React.ReactNode>> = {
		interview: <Mic size={iconSize} aria-hidden="true" />,
		notes: <NotebookPen size={iconSize} aria-hidden="true" />,
		document: <FileText size={iconSize} aria-hidden="true" />,
		imported: <FileUp size={iconSize} aria-hidden="true" />,
		automatic: <Link2 size={iconSize} aria-hidden="true" />,
	};

	return <span css={tileStyle(FILL_BY_KIND[kind], size)}>{icons[kind]}</span>;
};

export default ElementTile;
